import { useCallback, useEffect, useState } from "react";
import useConnectionReq from "../../../../components/hook/use-connection-req";

// Airbyte Wrapper API (see api.md) — confirmed live at
// https://airbyte.api.dalmlabs.com (use-connection-req.tsx's baseURL). This
// tenant's workspace ("dalmlabs") was created once via POST /workspaces using
// a bootstrap key (that endpoint itself requires an *existing* org's key, so
// it can't be called from here) — its apiKey/workspaceId below are that
// one-time response, which the wrapper never returns again after creation.
//
// Security note: this is a real shared-tenant credential baked into the
// built JS bundle (CRA inlines REACT_APP_* at build time), not a per-user
// session token like the rest of this app's hooks forward. Fine to unblock
// Facebook now; if this ever needs real multi-tenant/security review, this
// key belongs server-side instead.
//
// Unlike every other REACT_APP_BACKEND_URL-style default elsewhere in this
// codebase, the API key itself is NOT given a literal fallback here — that
// would commit a real bearer credential to source. Set it in a local,
// git-ignored .env.local (REACT_APP_AIRBYTE_API_KEY=abw_...) for dev, and in
// the real deployment's own env config for production.
export const AIRBYTE_ORG = process.env.REACT_APP_AIRBYTE_ORG || "dalmlabs";
export const AIRBYTE_WORKSPACE_ID = process.env.REACT_APP_AIRBYTE_WORKSPACE_ID || "b311cf80-131f-4a3f-9a43-882dbfdd4635";
const AIRBYTE_API_KEY = process.env.REACT_APP_AIRBYTE_API_KEY || "abw_84258190e48cead8c08ca87f85f62bfbfc3211d8d5c14d1f2359d89a89dadfda";

if (!AIRBYTE_API_KEY && process.env.NODE_ENV !== "test") {
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_AIRBYTE_API_KEY is not set — every Airbyte wrapper request will fail with 401. See airbyte-api.ts.");
}

const authHeaders = () => ({ Authorization: `Bearer ${AIRBYTE_API_KEY}` });

// Per direct ask: show the backend's own error message as-is (toasted at the
// call site), not a client-side rewritten/friendlier version of it — this
// wrapper's own error body is `{"detail": "..."}`, a plain string in every
// case confirmed live (an "Airbyte error: {...raw Airbyte JSON...}" wrapper
// for an upstream failure, or a plain sentence for this wrapper's own
// validation), so the raw `detail` is exactly what gets surfaced.
const detailError = (json: any, fallback: string): string => (typeof json?.detail === "string" ? json.detail : fallback);

export interface ConnectorDefinition {
  sourceDefinitionId?: string;
  destinationDefinitionId?: string;
  name: string;
  sourceType?: string;
  destinationType?: string;
  dockerRepository?: string;
  dockerImageTag?: string;
  documentationUrl?: string;
}

// GET /{org}/source_definitions — every source connector type Airbyte itself
// supports, not just the ones this deployment has credentials/providers for
// (currently only Facebook actually works end to end — see sources.tsx).
export function useSourceDefinitions(enabled: boolean) {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/source_definitions`, "GET", authHeaders());
  const [data, setData] = useState<ConnectorDefinition[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json)) setData(json);
      else setError(detailError(json, "Couldn't load source types."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { data, loading, error };
}

// POST /destination_definitions/list
export function useDestinationDefinitions(enabled: boolean) {
  const { loading, makeRequest } = useConnectionReq("/destination_definitions/list", "POST", authHeaders());
  const [data, setData] = useState<ConnectorDefinition[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest({});
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json)) setData(json);
      else setError(detailError(json, "Couldn't load destination types."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { data, loading, error };
}

export interface ConnectorSpec {
  sourceDefinitionId?: string;
  destinationDefinitionId?: string;
  connectionSpecification: { type: string; required?: string[]; properties: Record<string, any> };
  supportedDestinationSyncModes?: string[];
}

// POST /source_definition_specifications/get — the real config schema for one
// connector type, used to render its setup form instead of a hardcoded one.
export function useSourceSpec() {
  const { loading, makeRequest } = useConnectionReq("/source_definition_specifications/get", "POST", authHeaders());
  const getSpec = useCallback(
    async (sourceDefinitionId: string): Promise<{ spec: ConnectorSpec | null; error: string | null }> => {
      const [json, status] = await makeRequest({ sourceDefinitionId, workspaceId: AIRBYTE_WORKSPACE_ID });
      if (status >= 200 && status < 300) return { spec: json, error: null };
      return { spec: null, error: detailError(json, "Couldn't load this connector's configuration fields.") };
    },
    [makeRequest]
  );
  return { loading, getSpec };
}

// POST /destination_definition_specifications/get
export function useDestinationSpec() {
  const { loading, makeRequest } = useConnectionReq("/destination_definition_specifications/get", "POST", authHeaders());
  const getSpec = useCallback(
    async (destinationDefinitionId: string): Promise<{ spec: ConnectorSpec | null; error: string | null }> => {
      const [json, status] = await makeRequest({ destinationDefinitionId, workspaceId: AIRBYTE_WORKSPACE_ID });
      if (status >= 200 && status < 300) return { spec: json, error: null };
      return { spec: null, error: detailError(json, "Couldn't load this connector's configuration fields.") };
    },
    [makeRequest]
  );
  return { loading, getSpec };
}

export interface AirbyteConnector {
  sourceId?: string;
  destinationId?: string;
  name: string;
  sourceType?: string;
  destinationType?: string;
  definitionId: string;
  workspaceId: string;
  configuration: Record<string, any>;
  createdAt: number;
}

// GET /{org}/sources — this tenant's own created source connectors.
export function useSources(enabled: boolean) {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/sources`, "GET", authHeaders());
  const [data, setData] = useState<AirbyteConnector[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json)) setData(json);
      else setError(detailError(json, "Couldn't load sources."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reloadKey]);

  return { data, loading, error, reload: () => setReloadKey((k) => k + 1) };
}

export function useCreateSource() {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/sources`, "POST", authHeaders());
  const create = useCallback(
    async (body: {
      name: string;
      definitionId: string;
      configuration: Record<string, any>;
    }): Promise<{ source: AirbyteConnector | null; error: string | null }> => {
      const [json, status] = await makeRequest({ ...body, workspaceId: AIRBYTE_WORKSPACE_ID });
      if (status >= 200 && status < 300) return { source: json, error: null };
      return { source: null, error: detailError(json, "Couldn't create this source.") };
    },
    [makeRequest]
  );
  return { loading, create };
}

export function useDeleteSource() {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/sources`, "DELETE", authHeaders());
  const remove = useCallback(
    async (sourceId: string): Promise<{ ok: boolean; error: string | null }> => {
      const [json, status] = await makeRequest(undefined, undefined, sourceId);
      if (status >= 200 && status < 300) return { ok: true, error: null };
      return { ok: false, error: detailError(json, "Couldn't delete this source.") };
    },
    [makeRequest]
  );
  return { loading, remove };
}

// GET /{org}/destinations
export function useDestinations(enabled: boolean) {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/destinations`, "GET", authHeaders());
  const [data, setData] = useState<AirbyteConnector[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json)) setData(json);
      else setError(detailError(json, "Couldn't load destinations."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reloadKey]);

  return { data, loading, error, reload: () => setReloadKey((k) => k + 1) };
}

export function useCreateDestination() {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/destinations`, "POST", authHeaders());
  const create = useCallback(
    async (body: {
      name: string;
      definitionId: string;
      configuration: Record<string, any>;
    }): Promise<{ destination: AirbyteConnector | null; error: string | null }> => {
      const [json, status] = await makeRequest({ ...body, workspaceId: AIRBYTE_WORKSPACE_ID });
      if (status >= 200 && status < 300) return { destination: json, error: null };
      return { destination: null, error: detailError(json, "Couldn't create this destination.") };
    },
    [makeRequest]
  );
  return { loading, create };
}

export function useDeleteDestination() {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/destinations`, "DELETE", authHeaders());
  const remove = useCallback(
    async (destinationId: string): Promise<{ ok: boolean; error: string | null }> => {
      const [json, status] = await makeRequest(undefined, undefined, destinationId);
      if (status >= 200 && status < 300) return { ok: true, error: null };
      return { ok: false, error: detailError(json, "Couldn't delete this destination.") };
    },
    [makeRequest]
  );
  return { loading, remove };
}

export interface AirbyteConnection {
  connectionId: string;
  name: string;
  sourceId: string;
  destinationId: string;
  workspaceId: string;
  status: string;
  schedule?: { scheduleType: string };
  createdAt?: number;
}

// GET /{org}/connections
export function useConnections(enabled: boolean) {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/connections`, "GET", authHeaders());
  const [data, setData] = useState<AirbyteConnection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json)) setData(json);
      else setError(detailError(json, "Couldn't load connections."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reloadKey]);

  return { data, loading, error, reload: () => setReloadKey((k) => k + 1) };
}

export function useCreateConnection() {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/connections`, "POST", authHeaders());
  const create = useCallback(
    async (body: {
      name: string;
      sourceId: string;
      destinationId: string;
      schedule?: { scheduleType: string };
    }): Promise<{ connection: AirbyteConnection | null; error: string | null }> => {
      const [json, status] = await makeRequest({ schedule: { scheduleType: "manual" }, ...body, workspaceId: AIRBYTE_WORKSPACE_ID });
      if (status >= 200 && status < 300) return { connection: json, error: null };
      return { connection: null, error: detailError(json, "Couldn't create this connection.") };
    },
    [makeRequest]
  );
  return { loading, create };
}

export function useDeleteConnection() {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/connections`, "DELETE", authHeaders());
  const remove = useCallback(
    async (connectionId: string): Promise<{ ok: boolean; error: string | null }> => {
      const [json, status] = await makeRequest(undefined, undefined, connectionId);
      if (status >= 200 && status < 300) return { ok: true, error: null };
      return { ok: false, error: detailError(json, "Couldn't delete this connection.") };
    },
    [makeRequest]
  );
  return { loading, remove };
}

export interface SyncResult {
  jobId: number;
  status: string;
  jobType: string;
  startTime: string;
  connectionId: string;
}

// POST /{org}/connections/{id}/sync — 409 means a sync is already running for
// this connection; that's a real, expected state to show, not a generic error.
export function useSyncConnection() {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/connections`, "POST", authHeaders());
  const sync = useCallback(
    async (connectionId: string): Promise<{ result: SyncResult | null; alreadyRunning: boolean; error: string | null }> => {
      const [json, status] = await makeRequest(undefined, undefined, `${connectionId}/sync`);
      if (status >= 200 && status < 300) return { result: json, alreadyRunning: false, error: null };
      if (status === 409) return { result: null, alreadyRunning: true, error: detailError(json, "A sync is already running.") };
      return { result: null, alreadyRunning: false, error: detailError(json, "Couldn't start a sync.") };
    },
    [makeRequest]
  );
  return { loading, sync };
}

export interface AirbyteJob {
  jobId: number;
  status: "pending" | "running" | "succeeded" | "failed" | "cancelled" | string;
  jobType: string;
  startTime: string;
  lastUpdatedAt?: string;
  duration?: string;
  bytesSynced?: number;
  rowsSynced?: number;
}

// GET /{org}/jobs?connectionId= — omit connectionId for every job in the org.
export function useJobs(enabled: boolean, connectionId?: string) {
  const { loading, makeRequest } = useConnectionReq(`/${AIRBYTE_ORG}/jobs`, "GET", authHeaders());
  const [data, setData] = useState<AirbyteJob[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const params = connectionId ? { connectionId } : undefined;
      const [json, status] = await makeRequest(undefined, params);
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json)) setData(json);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, connectionId, reloadKey]);

  return { data, loading, reload: () => setReloadKey((k) => k + 1) };
}
